import React, { useState } from 'react';
import { FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getCaptchaToken, verifyCaptcha } from '../utils/captcha';
import { downloadReport } from '../utils/api';
import { useData } from '../context/DataContext';

const ReportsPage = () => {
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [downloadForm, setDownloadForm] = useState({
    name: '',
    email: '',
    company: '',
    role: ''
  });

  const { data } = useData();
  const reports = [...(data?.reportsData || [])].sort((a, b) => {
    return String(b.id).localeCompare(String(a.id), undefined, { numeric: true });
  });

  const handleGetReport = (report) => {
    setSelectedReport(report);
    setShowDownloadForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // reCAPTCHA v3 verification
      const token = await getCaptchaToken('report_download');
      const captchaResult = await verifyCaptcha(token, 'report_download');
      if (!captchaResult.success) {
        toast.error('Verification failed. Please try again.');
        return;
      }

      await downloadReport({
        reportId: selectedReport?.id,
        ...downloadForm
      });
      toast.success('Thank you! Check your email for the download link.');
      setDownloadForm({ name: '', email: '', company: '', role: '' });
      setShowDownloadForm(false);
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Failed to request report. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      <Header />
      {/* Hero Section */}
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-amber-500/10 text-amber-500 border-amber-500/20">Insights</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Research Reports
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              In-depth research and analysis on fundraising, M&A, and market trends across sectors
            </p>
          </div>
        </div>
      </section>

      {/* Reports Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            {reports.map((report) => (
              <Card key={report.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all">
                <CardHeader>
                  <div className="w-16 h-16 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-amber-500" />
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="border-slate-600 text-slate-400">{report.pages}</Badge>
                    <Badge variant="outline" className="border-slate-600 text-slate-400">{report.published}</Badge>
                  </div>
                  <CardTitle className="text-xl text-white mb-2">{report.title}</CardTitle>
                  <CardDescription className="text-slate-400 leading-relaxed">
                    {report.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    onClick={() => handleGetReport(report)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Get the Report
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Download Form Modal */}
      {showDownloadForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-slate-800 border-slate-700 max-w-2xl w-full">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Get the Report</CardTitle>
              <CardDescription className="text-slate-400">
                {selectedReport?.title}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Your Name *"
                    value={downloadForm.name}
                    onChange={(e) => setDownloadForm({...downloadForm, name: e.target.value})}
                    required
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                  <Input
                    type="email"
                    placeholder="Email *"
                    value={downloadForm.email}
                    onChange={(e) => setDownloadForm({...downloadForm, email: e.target.value})}
                    required
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Company / Organization"
                    value={downloadForm.company}
                    onChange={(e) => setDownloadForm({...downloadForm, company: e.target.value})}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                  <Input
                    placeholder="Your Role"
                    value={downloadForm.role}
                    onChange={(e) => setDownloadForm({...downloadForm, role: e.target.value})}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1 border-slate-600 text-slate-300"
                    onClick={() => setShowDownloadForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                    <Download className="mr-2 h-4 w-4" />
                    Download Report
                  </Button>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  The report will be sent to your email address
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default ReportsPage;
